import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { FileText } from 'lucide-react';

interface FloatingActionButtonProps {
  onClick: () => void;
  label: string;
  icon?: React.ReactNode;
  className?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'custom';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'blue' | 'green' | 'red' | 'purple' | 'gray' | 'indigo';
}

const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  onClick,
  label,
  icon,
  className = '',
  position = 'bottom-right',
  size = 'sm',
  color = 'blue',
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const positionClasses = {
    'bottom-right': 'fixed bottom-8 right-8',
    'bottom-left': 'fixed bottom-8 left-8',
    'top-right': 'fixed top-8 right-8',
    'top-left': 'fixed top-8 left-8',
    'custom': 'absolute',
  };

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-20 h-20',
    lg: 'w-24 h-24',
    xl: 'w-28 h-28',
  };

  const iconSizes = {
    sm: 20,
    md: 24,
    lg: 28,
    xl: 32,
  };

  const colorClasses = {
    blue: 'bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 focus:ring-blue-400 shadow-blue-500/25',
    green: 'bg-gradient-to-br from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 focus:ring-green-400 shadow-green-500/25',
    red: 'bg-gradient-to-br from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 focus:ring-red-400 shadow-red-500/25',
    purple: 'bg-gradient-to-br from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 focus:ring-purple-400 shadow-purple-500/25',
    gray: 'bg-gradient-to-br from-gray-500 to-gray-700 hover:from-gray-600 hover:to-gray-800 focus:ring-gray-400 shadow-gray-500/25',
    indigo: 'bg-gradient-to-br from-indigo-500 to-indigo-700 hover:from-indigo-600 hover:to-indigo-800 focus:ring-indigo-400 shadow-indigo-500/25',
  };

  const getTooltipPosition = () => {
    return 'bottom-full mb-2';
  };

  const getTooltipAlign = () => {
    return 'left-1/2 transform -translate-x-1/2';
  };

  return (
    <div className={`${positionClasses[position]} ${sizeClasses[size]}`}>
      {/* Tooltip */}
      {showTooltip && (
        <motion.div
          className={`
            absolute ${getTooltipPosition()} ${getTooltipAlign()}
            px-2 py-1 text-xs font-medium text-white bg-gray-900 
            rounded-md shadow-lg whitespace-nowrap z-50
          `}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.15 }}
        >
          {label}
          {/* Tooltip arrow */}
          <div 
            className="absolute w-2 h-2 bg-gray-900 transform rotate-45 top-full -mt-1 left-1/2 -ml-1"
          />
        </motion.div>
      )}

      {/* FAB Button */}
      <motion.button
        className={`
          w-full h-full
          ${colorClasses[color]}
          text-white 
          rounded-full 
          flex items-center justify-center
          shadow-2xl hover:shadow-3xl 
          transition-all duration-300 ease-out
          focus:outline-none focus:ring-4 focus:ring-opacity-50
          z-50 
          border-0 
          p-0
          ${className}
        `}
        style={{
          aspectRatio: '1',
          borderRadius: '50%',
        }}
        initial={{ scale: 0, opacity: 0, rotate: -180 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        whileHover={{ 
          scale: 1.15, 
          rotate: 5,
          transition: { duration: 0.2 }
        }}
        whileTap={{ 
          scale: 0.9,
          rotate: -5,
          transition: { duration: 0.1 }
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 25,
          duration: 0.6
        }}
        onClick={onClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        aria-label={label}
      >
        <div className="flex items-center justify-center">
          {icon || <FileText size={iconSizes[size]} strokeWidth={1.5} />}
        </div>
      </motion.button>
    </div>
  );
};

export default FloatingActionButton;
